import { FC, useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Text,
  Radio,
  RadioGroup,
  Stack,
  HStack,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { IconAlertTriangle } from "@tabler/icons-react";
import { Button } from "@Components/Button";

// Opções oferecidas quando o arquivo não traz coluna de produto/item.
// O estudo é de diesel — ofertamos as variantes de diesel.
export const PRODUTO_OPCOES = [
  "DIESEL S-10",
  "DIESEL S-10 Aditivado",
  "DIESEL S-500",
];

interface ProdutoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (produto: string) => void;
}

/**
 * Fluxo quando o arquivo não tem coluna de item: nega o envio e pergunta se todos
 * os abastecimentos são do mesmo produto. Se sim, o consultor escolhe o produto de
 * uma lista e a análise é reenviada aplicando-o a todas as linhas.
 */
export const ProdutoModal: FC<ProdutoModalProps> = ({ isOpen, onClose, onConfirm }) => {
  const [etapa, setEtapa] = useState<"pergunta" | "escolha" | "nao">("pergunta");
  const [produto, setProduto] = useState(PRODUTO_OPCOES[0]);

  const fechar = () => {
    setEtapa("pergunta");
    setProduto(PRODUTO_OPCOES[0]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={fechar} isCentered>
      <ModalOverlay />
      <ModalContent fontFamily="body">
        <ModalHeader>
          <HStack spacing={2}>
            <Icon as={IconAlertTriangle} boxSize={5} color="states.warning.base" />
            <Text fontFamily="heading" fontSize="18px" color="neutrals.text.strong-950">
              Produto não identificado
            </Text>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          {etapa === "pergunta" && (
            <Text fontSize="sm" color="neutrals.text.sub-600">
              O arquivo não tem uma coluna de produto/item. Todos os abastecimentos são do{" "}
              <strong>mesmo produto</strong>?
            </Text>
          )}

          {etapa === "escolha" && (
            <>
              <Text fontSize="sm" color="neutrals.text.sub-600" mb="12px">
                Qual é o produto? Vamos aplicá-lo a todas as linhas.
              </Text>
              <RadioGroup value={produto} onChange={setProduto}>
                <Stack spacing={2}>
                  {PRODUTO_OPCOES.map((p) => (
                    <Radio key={p} value={p} colorScheme="red">
                      <Text fontSize="sm" color="neutrals.text.strong-950">
                        {p}
                      </Text>
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>

              <Flex
                mt="14px"
                gap={2}
                p="10px 12px"
                bg="states.warning.lighter"
                border="1px solid"
                borderColor="states.warning.light"
                borderRadius="8px"
              >
                <Icon as={IconAlertTriangle} boxSize={4} color="states.warning.base" mt="2px" />
                <Text fontSize="xs" color="states.warning.dark">
                  Só confirme se tiver <strong>certeza</strong>. O produto escolhido é aplicado a{" "}
                  <strong>todas as linhas</strong> e <strong>afeta muito o resultado</strong> —
                  preço de referência e economia. Na dúvida, use um arquivo com a coluna de produto.
                </Text>
              </Flex>
            </>
          )}

          {etapa === "nao" && (
            <Flex
              gap={3}
              p="12px"
              bg="states.warning.lighter"
              border="1px solid"
              borderColor="states.warning.light"
              borderRadius="8px"
            >
              <Icon as={IconAlertTriangle} boxSize={4} color="states.warning.base" mt="2px" />
              <Text fontSize="sm" color="states.warning.dark">
                Então o arquivo precisa ter uma <strong>coluna de produto/item por linha</strong>.
                Ajuste o arquivo e envie novamente.
              </Text>
            </Flex>
          )}
        </ModalBody>

        <ModalFooter>
          {etapa === "pergunta" && (
            <HStack spacing={2}>
              <Button colorSchema="neutral" variant="outline" size="sm" onClick={() => setEtapa("nao")}>
                Não
              </Button>
              <Button colorSchema="brand" variant="solid" size="sm" onClick={() => setEtapa("escolha")}>
                Sim, é o mesmo
              </Button>
            </HStack>
          )}
          {etapa === "escolha" && (
            <Button
              colorSchema="brand"
              variant="solid"
              size="sm"
              onClick={() => {
                onConfirm(produto);
                fechar();
              }}
            >
              Analisar como {produto}
            </Button>
          )}
          {etapa === "nao" && (
            <Button colorSchema="neutral" variant="outline" size="sm" onClick={fechar}>
              Entendi
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
